import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/store/auth';
// Pure helpers only (no top-level use), so the api <-> employeeRights import
// cycle is safe: both are read at call time, long after either module loads.
import { describeModuleRightDenial, MODULE_RIGHT_DENIED_CODE, RIGHTS_STALE_EVENT } from './employeeRights';

const API_BASE_URL = '/policy/api/v1';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
  // 9c: the refresh token is an httpOnly cookie now. Same-origin XHR sends
  // cookies anyway, but saying so explicitly keeps it working if the SPA is
  // ever served from a different origin than the API (CORS_ALLOW_CREDENTIALS
  // is already on server-side).
  withCredentials: true,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Shared in-flight refresh promise. When several requests 401 at once (e.g. a
// page firing multiple lookups on mount), they must AWAIT one refresh call
// instead of each independently POSTing /users/auth/refresh/ — refresh tokens
// are single-use (ROTATE_REFRESH_TOKENS + BLACKLIST_AFTER_ROTATION), so only
// the first concurrent refresh would succeed and the rest would 401 again.
let refreshPromise: Promise<string> | null = null;

/**
 * Endpoints that never trigger a token refresh (9c).
 *
 * Login is the obvious case: a wrong password answers 401, and refreshing on
 * it would sign the browser out and bounce a user who is already sitting on
 * the login page. The refresh endpoint itself matters more — it is POSTed with
 * bare axios precisely so its own 401 cannot re-enter this interceptor, and
 * this list is the second lock on that door.
 *
 * The endpoints are listed one by one rather than matched on the
 * `/users/auth/` prefix: `select-broker/` and `my-brokers/` live under that
 * same prefix but are ordinary IsAuthenticated calls made from the header's
 * BrokerSwitcher, and a prefix match would strip them of the silent
 * refresh-and-retry every other endpoint gets — a broker whose access token
 * aged past its hour would see the switcher fail instead of recover.
 */
const NO_REFRESH_PATHS = [
  '/users/auth/login/',
  '/users/auth/insurer/login/',
  '/users/auth/broker/login/',
  '/users/auth/channel/login/',
  '/users/auth/customer/login/',
  '/users/auth/refresh/',
  '/users/auth/logout/',
  '/users/auth/register/',
  '/users/otp/request/',
  '/users/otp/verify/',
];

function isAuthEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  // Compare on the path only: a query string must not stop the match, and a
  // trailing-slash endpoint cannot be a prefix of anything else.
  const path = url.split('?')[0];
  return NO_REFRESH_PATHS.some((endpoint) => path.endsWith(endpoint));
}

/** Web Locks name: one refresh at a time across every tab of this browser. */
const REFRESH_LOCK = 'policy-auth-refresh';

/**
 * Run `task` while holding the cross-tab refresh lock, if the browser has one.
 *
 * The in-flight promise above only coalesces refreshes inside ONE tab. The
 * refresh cookie is per-browser, so two tabs restoring at the same moment
 * (session restore, "reload all tabs", duplicating a tab) both send the same
 * cookie value and rotation retires it for whoever arrives second. Serialising
 * here means the second tab's request goes out after the first has replaced
 * the cookie, so it reads the new value and succeeds instead of 401ing.
 *
 * Web Locks is not universal, and the server no longer clears the cookie on a
 * rotation collision either, so a browser without it degrades to the previous
 * behaviour rather than breaking.
 */
function withRefreshLock<T>(task: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks?.request) return task();
  try {
    return locks.request(REFRESH_LOCK, task) as Promise<T>;
  } catch {
    return task();
  }
}

/**
 * A 401 that means "another tab refreshed first", not "your session is over".
 *
 * The server answers `token_superseded` when the token presented was valid but
 * had already been rotated away, and leaves the cookie alone in that case.
 */
function isSupersededRefresh(error: unknown): boolean {
  const response = (error as AxiosError | undefined)?.response;
  if (!response || response.status !== 401) return false;
  const data = response.data as { code?: unknown } | undefined;
  return data?.code === 'token_superseded';
}

/**
 * The refresh call was rate-limited, which is NOT "your session is over".
 *
 * Since 9c `restoreSession()` spends one refresh on every page load, and a
 * broker office behind one NATed address is a single IP for the whole staff.
 * The server now gives refresh its own, roomy scope (`token_refresh`), but a
 * 429 is still possible — and treating it like any other refresh failure was
 * the trap: the client signed the person out, and the sign-in page sits behind
 * the same bucket, so the office could not get back in. A throttle is a "come
 * back in a moment", so the session is left alone and the wait is surfaced.
 */
export class RefreshThrottledError extends Error {
  readonly retryAfterSeconds: number | null;

  constructor(retryAfterSeconds: number | null) {
    super(
      retryAfterSeconds
        ? `This network has made too many requests. Your session is still active — please wait about ${retryAfterSeconds} seconds and try again.`
        : 'This network has made too many requests. Your session is still active — please wait a moment and try again.'
    );
    this.name = 'RefreshThrottledError';
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** Seconds the server asked us to wait, from `Retry-After` or DRF's body. */
function retryAfterSeconds(error: unknown): number | null {
  const response = (error as AxiosError | undefined)?.response;
  const header = response?.headers?.['retry-after'];
  const fromHeader = Number(header);
  if (Number.isFinite(fromHeader) && fromHeader > 0) return Math.round(fromHeader);
  // DRF spells it out in the detail text ("...available in 42 seconds.").
  const detail = (response?.data as { detail?: unknown } | undefined)?.detail;
  if (typeof detail === 'string') {
    const match = detail.match(/(\d+)\s*second/i);
    if (match) return Number(match[1]);
  }
  return null;
}

function isThrottled(error: unknown): boolean {
  return (error as AxiosError | undefined)?.response?.status === 429;
}

/** The `user_id` claim of a JWT, or null if it cannot be read. */
function tokenUserId(token: string): string | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as { user_id?: unknown };
    return claims.user_id == null ? null : String(claims.user_id);
  } catch {
    return null;
  }
}

/**
 * Mint a new access token from the httpOnly refresh cookie.
 *
 * Nothing is read from the store any more: the credential is the cookie, which
 * this code cannot see and the browser attaches itself (`withCredentials`).
 * The server rotates the cookie on every call and returns only `access`.
 */
async function performRefresh(retryOnCollision = true): Promise<string> {
  let response;
  try {
    response = await axios.post(
      `${API_BASE_URL}/users/auth/refresh/`,
      {},
      // A timeout matters more than usual now: this call is made while holding
      // the cross-tab lock, and a request that hung forever would hold it.
      { withCredentials: true, timeout: 30000 }
    );
  } catch (error) {
    if (retryOnCollision && isSupersededRefresh(error)) {
      // Another tab rotated the cookie out from under this request. The value
      // the browser holds now is that tab's live one, so one more attempt
      // restores the session instead of signing everybody out. Bounded to a
      // single retry, and the server leaves the cookie in place on this path.
      return performRefresh(false);
    }
    if (isThrottled(error)) {
      // Rethrown as its own type so the two callers below can tell "wait a
      // moment" apart from "this cookie is dead" — the difference between
      // showing a retry and signing the whole office out.
      throw new RefreshThrottledError(retryAfterSeconds(error));
    }
    throw error;
  }

  const access = response.data?.access;
  if (!access) {
    // "Reports success, persists nothing" — a 200 with no token is a failed
    // refresh, and treating it as one is what stops the retried request going
    // out with no Authorization header at all.
    throw new Error('Refresh returned no access token');
  }

  // The cookie is per-browser, not per-tab: if somebody signs in as a second
  // account in another tab, this tab's refresh comes back holding THAT
  // account's access token while this tab still renders the first account's
  // user and tenant context. Adopting it silently would leave the screen
  // saying one identity and every request made as another, so the tab signs
  // itself out instead and the person signs in again on it.
  const expectedUserId = useAuthStore.getState().user?.id;
  const actualUserId = tokenUserId(access as string);
  if (expectedUserId && actualUserId && actualUserId !== expectedUserId) {
    useAuthStore.getState().logout();
    throw new Error('Refresh returned a token for a different account');
  }

  useAuthStore.getState().setAccessToken(access as string);
  return access as string;
}

export function refreshAccessToken(): Promise<string> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = withRefreshLock(() => performRefresh()).finally(() => {
    // Clear so the NEXT 401 (e.g. after this token also expires) starts a
    // fresh refresh instead of replaying a settled promise forever.
    refreshPromise = null;
  });

  return refreshPromise;
}

/**
 * 9c: restore a session on startup, before the first protected route renders.
 *
 * The access token is no longer persisted, so every reload begins with none.
 * The refresh cookie is httpOnly and invisible to this code, so the persisted
 * `isAuthenticated` flag is what says "somebody was signed in on this browser"
 * and is worth one refresh call. The cookie decides the outcome, not the flag:
 * if it has expired or been blacklisted the refresh 401s and the session is
 * cleared, which is exactly a clean sign-out.
 *
 * Returns whether the caller now holds an access token. Never throws, so App's
 * bootstrap cannot be left spinning.
 */
export async function restoreSession(): Promise<boolean> {
  const state = useAuthStore.getState();
  if (state.accessToken) return true;
  if (!state.isAuthenticated) return false;

  try {
    await refreshAccessToken();
    return true;
  } catch (error) {
    if (error instanceof RefreshThrottledError) {
      // The cookie was never judged: the request never reached the token
      // check. Clearing the session here would turn a rate limit into a
      // sign-out, and the login page is behind the same bucket. The persisted
      // `isAuthenticated` hint is left in place so the next page load (or the
      // next request's 401 retry) restores the session once the window rolls.
      return false;
    }
    useAuthStore.getState().logout();
    return false;
  }
}

/** B4: fired on any 403 so `useMyRights` refetches the caller's rights. */
export { RIGHTS_STALE_EVENT };
const MY_RIGHTS_URL = '/brokers/employee-rights/me/';
/** Server error code for a refused create on an expired broker licence (#324). */
export const BROKER_LICENCE_EXPIRED_CODE = 'broker_licence_expired';

/**
 * B4 "real time": a 403 is the moment we learn the rights this screen was
 * drawn with are out of date (an admin revoked something since the last
 * refetch). The interceptor cannot reach the QueryClient -- it lives in App.tsx
 * -- so it announces the fact and `useMyRights` invalidates on it.
 *
 * The `me` endpoint itself is excluded: a 403 there refetching `me` would be a
 * loop.
 *
 * A matrix refusal (`error.code === 'module_right_denied'`) is also reworded
 * with the labels the Rights screen uses, so every `getErrorMessage` caller
 * shows "You do not have Create access to Servicing Requests." rather than the
 * raw module code.
 */
function handleForbidden(error: AxiosError): void {
  const data = error.response?.data as { error?: { code?: unknown; message?: unknown } } | undefined;
  const errObj = data && typeof data === 'object' ? data.error : undefined;
  if (errObj && typeof errObj === 'object' && errObj.code === MODULE_RIGHT_DENIED_CODE) {
    errObj.message = describeModuleRightDenial(
      typeof errObj.message === 'string' ? errObj.message : undefined
    );
  }

  // #324: an expired broker licence is a business refusal, not a rights change.
  // The server's sentence names the licence date and is shown as-is by
  // `getErrorMessage`; re-fetching the user's rights would achieve nothing.
  if (errObj && typeof errObj === 'object' && errObj.code === BROKER_LICENCE_EXPIRED_CODE) return;

  const url = error.config?.url ?? '';
  if (url.includes(MY_RIGHTS_URL)) return;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(RIGHTS_STALE_EVENT, {
        detail: { url, code: errObj && typeof errObj === 'object' ? errObj.code : undefined },
      })
    );
  }
}

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // If 401 and we haven't retried yet. `_retry` is per-request, so a request
    // can be replayed at most once; the auth endpoints are excluded entirely
    // so a failed sign-in or refresh can never start a refresh of its own.
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthEndpoint(originalRequest?.url)
    ) {
      originalRequest._retry = true;

      try {
        const access = await refreshAccessToken();

        // Retry the original request
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return api(originalRequest);
      } catch (refreshError) {
        if (refreshError instanceof RefreshThrottledError) {
          // A rate limit says nothing about whether the session is valid, so
          // the session stays and the caller gets a message it can show
          // (`getErrorMessage` falls through to `error.message`). Bouncing to
          // /login here was the lockout: the login endpoint is behind the same
          // per-IP bucket, so the office would land on a page that could not
          // sign them in either.
          return Promise.reject(refreshError);
        }
        useAuthStore.getState().logout();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    if (error.response?.status === 403) {
      handleForbidden(error);
    }

    return Promise.reject(error);
  }
);

export default api;

/**
 * Recursively flatten a DRF-style error value into a flat ``{ "dotted.key": message }``
 * map. DRF nests validation errors from nested serializers (e.g. a ``proposer``
 * block) one level per block: ``{"proposer": {"phone": ["required"]}}`` —
 * without flattening, that nested object gets coerced to the literal string
 * ``"[object Object]"`` wherever a leaf message was expected. Handles
 * arbitrary nesting depth (e.g. a nested block inside a nested block).
 */
function flattenDRFErrors(value: unknown, prefix = ''): Record<string, string> {
  const out: Record<string, string> = {};
  if (Array.isArray(value)) {
    if (prefix && value.length > 0) {
      out[prefix] = String(value[0]);
    }
    return out;
  }
  if (value && typeof value === 'object') {
    for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
      const nextKey = prefix ? `${prefix}.${key}` : key;
      Object.assign(out, flattenDRFErrors(nested, nextKey));
    }
    return out;
  }
  if (prefix && typeof value === 'string') {
    out[prefix] = value;
  }
  return out;
}

// Helper function to handle API errors
// Backend returns: { success: false, error: { code, message, fields, status_code } }
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as Record<string, unknown> | undefined;
    if (data) {
      // Some endpoints (commissions statement upload, broker-rate CSV) answer
      // with a bare `{"error": "<message>"}`. Without this branch the string
      // has no `.message`, every later branch misses, and the user is shown
      // axios's own "Request failed with status code 400".
      if (typeof data.error === 'string' && data.error) {
        return data.error;
      }
      // Custom exception handler format: { success: false, error: { message, fields } }
      const errObj = data.error as Record<string, unknown> | undefined;
      if (errObj) {
        // Prefer the top-level message. The backend's
        // plain_language_exception_handler (global rule 6) regenerates it from
        // the humanised per-field text whenever `fields` is present, so it is
        // already user-safe -- unlike `fields`, which stays keyed by the raw
        // field name on purpose (getFieldErrors depends on that key for
        // per-field form highlighting). Concatenating that raw key into
        // display text (`${fieldName}: ${message}`) is exactly the jargon
        // leak global rule 6 exists to prevent (e.g. "accident_type: Please
        // select a valid Accident Type."), so it must never happen here.
        if (typeof errObj.message === 'string' && errObj.message) {
          return errObj.message;
        }
        // Fallback for a payload with fields but no top-level message: use
        // the flattened leaf message only, never the field-name prefix.
        const fields = errObj.fields as Record<string, unknown> | undefined;
        if (fields) {
          const firstField = Object.entries(flattenDRFErrors(fields))[0];
          if (firstField) {
            return firstField[1];
          }
        }
      }
      // Standard DRF format
      if (typeof data.detail === 'string') {
        return data.detail;
      }
      if (typeof data.message === 'string') {
        return data.message;
      }
      // Field errors object
      const errors = data.errors as Record<string, string[]> | undefined;
      if (errors) {
        const firstError = Object.values(errors)[0];
        if (Array.isArray(firstError) && firstError.length > 0) {
          return firstError[0];
        }
      }
    }
    if (error.message) {
      return error.message;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

// Conventional key under which non-field / top-level errors are collected.
export const NON_FIELD_KEY = '_';

function pushMessage(target: Record<string, string>, key: string, value: unknown): void {
  if (Array.isArray(value)) {
    if (key in target) return; // keep the first message per field
    if (value.length > 0) target[key] = String(value[0]);
  } else if (typeof value === 'string') {
    if (key in target) return; // keep the first message per field
    target[key] = value;
  } else if (value && typeof value === 'object') {
    // Nested serializer block (e.g. { proposer: { phone: [...] } }) — flatten
    // onto dotted keys ("proposer.phone") so a real message surfaces instead
    // of silently dropping it or stringifying to "[object Object]".
    for (const [nestedKey, message] of Object.entries(flattenDRFErrors(value, key))) {
      if (nestedKey in target) continue; // keep the first message per field
      target[nestedKey] = message;
    }
  }
}

/**
 * Parse a 4xx/409 response body into a flat ``{ fieldName: message }`` map.
 *
 * Handles the platform's standardized exception-handler shape
 * (``{ success: false, error: { message, code, fields } }``), plain DRF
 * serializer errors (``{ field: ["msg", ...] }``), and the ``{ detail: "msg" }``
 * shape. Non-field / top-level errors (DRF ``non_field_errors``, ``detail`` and
 * the standardized ``error.message``) are collected under ``NON_FIELD_KEY``.
 *
 * Never throws; returns an empty object when nothing can be parsed.
 */
export function getFieldErrors(error: unknown): Record<string, string> {
  const result: Record<string, string> = {};
  if (!axios.isAxiosError(error)) return result;
  const data = error.response?.data as Record<string, unknown> | undefined;
  if (!data || typeof data !== 'object') return result;

  // Standardized exception-handler shape: { error: { message, fields } }
  const errObj = data.error as Record<string, unknown> | undefined;
  if (errObj && typeof errObj === 'object') {
    const fields = errObj.fields as Record<string, unknown> | undefined;
    if (fields && typeof fields === 'object') {
      for (const [key, value] of Object.entries(fields)) {
        if (key === 'non_field_errors') {
          pushMessage(result, NON_FIELD_KEY, value);
        } else {
          pushMessage(result, key, value);
        }
      }
    }
    // Use the generic top-level message only when no specific errors were found,
    // so it stays out of summaries that already list per-field messages.
    if (Object.keys(result).length === 0 && typeof errObj.message === 'string') {
      result[NON_FIELD_KEY] = errObj.message;
    }
    return result;
  }

  // Plain DRF shapes: { detail } and { field: [messages] } / { field: "msg" }
  for (const [key, value] of Object.entries(data)) {
    if (key === 'success' || key === 'detail') continue;
    if (key === 'non_field_errors') {
      pushMessage(result, NON_FIELD_KEY, value);
    } else {
      pushMessage(result, key, value);
    }
  }
  if (!(NON_FIELD_KEY in result) && typeof data.detail === 'string') {
    result[NON_FIELD_KEY] = data.detail;
  }

  return result;
}
